import { Component, computed, EventEmitter, Input, Output, Signal, signal, ViewChild } from '@angular/core';
import { Agent, AgentKeys, LayoutGridType, TShirtSizesKeys } from '@ag-common-lib/public-api';
import { SizesService } from './sizes.service';
import { validateDxGroups } from 'ag-common-svc/lib/utils/validation';
import { ModalWindowComponent } from 'ag-common-svc/lib/components/modal-window/modal-window.component';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { InitializedEvent } from 'devextreme/ui/form';

@Component({
  // eslint-disable-next-line
  selector: 'ag-shr-sizes',
  templateUrl: './sizes.component.html',
  styleUrls: ['./sizes.component.scss'],
  providers: [SizesService],
})
export class SizesComponent {
  // eslint-disable-next-line
  @Input('agent') set _agent(value: Agent) {
    this.agent = value;
    this.funStaffFormDetails = this.sizesService.getFormData(value);
  }
  @Input() hasPermissionsToEdit: boolean = true;
  @Input() hasPermissionsToDelete: boolean = true;
  @Input() hasPermissionsToAdd: boolean = true;
  @Input() caption: string = 'Sizes for Fun Stuff';
  @Input() type: LayoutGridType = LayoutGridType.short;
  @Input() isFullWidth: boolean = false;
  // eslint-disable-next-line
  @Output() onFieldsUpdated: EventEmitter<{ agentId: string; updates: Partial<Agent> }> = new EventEmitter();
  @ViewChild('sizeModalRef', { static: true }) sizeModalComponent: ModalWindowComponent;

  protected readonly AgentKeys = AgentKeys;
  protected readonly TShirtSizesKeys = TShirtSizesKeys;
  protected readonly LayoutGridType = LayoutGridType;
  funStaffFormDetails;
  inProgress: Signal<boolean>;

  protected readonly validationGroup = 'validationGroup';
  protected readonly shirtSeizesFormValidationGroup = 'shirtSeizesFormValidationGroup';

  private agent: Agent;

  protected formSizeSig = signal<number | null>(null);

  constructor(private sizesService: SizesService) {
    this.inProgress = toSignal(sizesService.inProgress$, { initialValue: false });
  }

  public saveAgentUpdates = async () => {
    const isValid = await validateDxGroups([this.validationGroup, this.shirtSeizesFormValidationGroup]);

    if (isValid) {
      const agentId = this.agent[BaseModelKeys.dbId];
      this.sizesService.handleSave(agentId).then(updates => {
        this.sizeModalComponent.hideModal();
        this.onFieldsUpdated.emit({ agentId, updates });
      });
    }
  };

  public showEditorModal = () => {
    this.sizeModalComponent.showModal();
  };

  public handleClosePopup = e => {
    this.sizesService.onCancelEdit(e);
  };

  public get agentDbId(): string | null {
    return this.agent?.[BaseModelKeys.dbId] ?? null;
  }

  protected onFormInitialized = (e: InitializedEvent) => {
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === e.element) {
          this.formSizeSig.set(entry.contentRect.width);
          break;
        }
      }
    });

    resizeObserver.observe(e.element);
  };

  protected colCountByModalSig = computed(() => {
    const w = this.formSizeSig() ?? 0;

    const isWide = w >= 900;
    const isMedium = w >= 640;

    if (isWide) return { xs: 1, sm: 1, md: 1, lg: 3, xl: 3 } as const;
    if (isMedium) return { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 } as const;

    return { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 } as const;
  });
}
